// What the drive's listings say about a file without opening it: the kind of thing it is, judged
// from its name, and its size and date written the way a row prints them.

// The folder the API files uploads into when none is named. It belongs to the system, not to the
// person, so it is never listed as one of their folders.
export const ROOT_FOLDER = '.'

const KINDS = {
  image: ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'heic', 'heif', 'avif'],
  video: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
  audio: ['m4a', 'mp3', 'wav', 'aac', 'flac', 'ogg', 'oga'],
  pdf: ['pdf'],
  doc: ['md', 'txt', 'doc', 'docx', 'rtf', 'xlsx', 'csv'],
  code: ['js', 'jsx', 'ts', 'rs', 'sql', 'yml', 'yaml', 'json', 'sh']
}

export const fileExtension = filename => (filename.includes('.') ? filename.split('.').pop().toLowerCase() : '')

export const fileKind = filename => {
  const extension = fileExtension(filename)

  return Object.keys(KINDS).find(kind => KINDS[kind].includes(extension)) || 'file'
}

// The status an upload reaches once its encrypted copy is stored. Until then there is nothing the
// API can open, so nothing to read or download.
export const isSecured = file => file.status === 'uploaded'

// Still on its way to being secured, rather than secured or stopped for good as failed or blocked.
// A file moving between the drive and Locked is secured again for the side it goes to, "resealing",
// and one imported from Google Drive is still being copied in, "importing".
const SECURING = ['uploading', 'scanning', 'encrypting', 'encrypted', 'resealing', 'importing']

export const isSecuring = file => SECURING.includes(file.status)

export const isResealing = file => file.status === 'resealing'

// Stopped for good without being secured. It stays where it is, holding its name, until deleted.
export const isFailed = file => ['failed', 'virus'].includes(file.status)

// Documents the reader can show as they are. Word files, spreadsheets and rich text need
// something that understands their format, so they are left to download.
const PLAIN_TEXT = ['md', 'txt', 'csv']

// How the preview shows a file: as media, an image, a PDF, code, or plain text — or null when the
// drive cannot read inside it.
export const previewKind = filename => {
  const kind = fileKind(filename)

  if (kind === 'doc') return PLAIN_TEXT.includes(fileExtension(filename)) ? 'text' : null

  return kind === 'file' ? null : kind
}

// The worker makes a thumbnail of every kind the preview can read: a picture, a still, a page, a
// waveform or an excerpt.
export const getsThumbnail = filename => previewKind(filename) !== null

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

export const formatBytes = bytes => {
  if (bytes === null) return '—'

  let value = bytes
  let unit = 0

  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }

  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${UNITS[unit]}`
}

// The API writes timestamps the way chrono displays them — "2026-08-04 09:12:44.123 UTC" — which
// is not a format Date is required to understand.
export const parseTimestamp = timestamp => new Date(timestamp.replace(' UTC', 'Z').replace(' ', 'T'))

export const formatTimestamp = timestamp => {
  const date = parseTimestamp(timestamp)

  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}
